import React from 'react';
import { UseFormSetError } from '@fiverr-private/forms';
import { Link } from '@fiverr-private/typography';
import { useIdentificationContext, submitCheckoutV2 } from '@fiverr-private/identification_core';
import { useStepsContext } from '../../../StepsContext';
import { signIn } from '../../../../services/signIn';
import { FormSchema, PASSWORD_VISIBLE_ERROR } from '../../EmailIdentificationStep.constants';
import { LEARN_MORE_LINK } from '../useIdentificationStepForm/useIdentificationStepForm.constants';
import { appTranslate } from '../../../../i18n';
import { raiseGeneralErrorToast } from '../../../../shared/utils/raiseGeneralErrorToast';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../../../events';

interface Props {
    isSignUp: boolean;
    setError: UseFormSetError<FormSchema>;
    setLoading: (loading: boolean) => void;
}

export const useSubmitHandler = ({ isSignUp, setError, setLoading }: Props) => {
    const { goToStep } = useStepsContext();
    const { setEmail, setMode, setPassword, finishSigningFlowAndNavigate, setRedirectUrl } = useIdentificationContext();

    const onSubmit = async (data: FormSchema) => {
        setLoading(true);

        globalGrafanaReporter.count('identification.%{platform}.email_identification.continue');
        globalMixpanelReporter.send('Identification - Email Identification - Continue');
        globalBigQueryReporter.send('identification.email_identification.continue');

        if (isSignUp) {
            setEmail(data.usernameOrEmail);
            setPassword(data.password);
            setMode('sign_up');

            goToStep('profile_setup');
            return;
        }

        try {
            const {
                redirectUrl,
                guestCheckout,
                errorKeys = [],
            } = await signIn({
                login: data.usernameOrEmail,
                password: data.password,
                funnel: 'standard',
            });

            if (errorKeys.length) {
                const [errorKey] = errorKeys;

                setError('password', {
                    message: appTranslate<string>(`validation.password.${errorKey}`, {
                        templates: {
                            learn_more_link: (text?: string) => <Link href={LEARN_MORE_LINK}>{text}</Link>,
                        },
                    }),
                    type: PASSWORD_VISIBLE_ERROR,
                });
                return;
            }

            if (submitCheckoutV2('login', guestCheckout)) {
                return;
            }

            setMode('sign_in');

            if (redirectUrl) {
                setRedirectUrl(redirectUrl);
            }

            globalGrafanaReporter.count('identification.%{platform}.signin.completed');
            // Should be removed. Duplicating in "finishSigningFlowAndNavigate"
            globalMixpanelReporter.send('Identification - Completed');
            globalBigQueryReporter.send('identification.completed');

            await finishSigningFlowAndNavigate();
        } catch (error) {
            globalMixpanelReporter.send('Identification - Failed', {
                errorType: error instanceof Error ? error.message : 'unspecified',
            });
            globalBigQueryReporter.send('identification.failed');
            globalGrafanaReporter.count('identification.%{platform}.signin.failed');
            raiseGeneralErrorToast();
        } finally {
            setLoading(false);
        }
    };

    return onSubmit;
};
